import { FileText, Trash2, Upload } from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useRef, useState, type ChangeEvent } from "react";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";

import { formatLocalizedDate } from "../misc/RelativeDate";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { CompanyQuote } from "../types";

const PDF_TYPE = "application/pdf";
const MAX_FILE_MB = 20;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

/** « Devis » tab of a company: upload PDF quotes and open or delete them. */
export const CompanyQuotes = ({ companyId }: { companyId: Identifier }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: quotes = [], refetch } = useGetList<CompanyQuote>(
    "company_quotes",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "date", order: "DESC" },
      filter: { company_id: companyId },
    },
  );
  const [create, { isPending: isUploading }] = useCreate();

  const handleFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    const rejected = files.filter(
      (file) => file.type !== PDF_TYPE || file.size > MAX_FILE_BYTES,
    );
    if (rejected.length) {
      notify("resources.companies.quotes.invalid_file", {
        type: "warning",
        messageArgs: { name: rejected[0].name, max: MAX_FILE_MB },
      });
    }
    const pdfs = files.filter((file) => !rejected.includes(file));
    try {
      for (const file of pdfs) {
        await create(
          "company_quotes",
          {
            data: {
              company_id: companyId,
              date: new Date().toISOString(),
              attachments: [
                {
                  rawFile: file,
                  src: URL.createObjectURL(file),
                  title: file.name,
                  type: file.type,
                },
              ],
            },
          },
          { returnPromise: true },
        );
      }
      if (pdfs.length) notify("resources.companies.quotes.added");
    } catch {
      notify("resources.companies.quotes.upload_error", { type: "error" });
    }
    refetch();
  };

  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="flex justify-end">
        <input
          ref={inputRef}
          type="file"
          accept={PDF_TYPE}
          multiple
          className="hidden"
          aria-label={translate("resources.companies.quotes.upload")}
          onChange={handleFiles}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="size-4" />
          {translate("resources.companies.quotes.upload")}
        </Button>
      </div>
      {quotes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.companies.quotes.empty")}
        </p>
      ) : (
        <ul className="divide-y">
          {quotes.map((quote) => (
            <QuoteRow key={quote.id} quote={quote} onDeleted={refetch} />
          ))}
        </ul>
      )}
    </div>
  );
};

const QuoteRow = ({
  quote,
  onDeleted,
}: {
  quote: CompanyQuote;
  onDeleted: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "fr"] = useLocaleState();
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const salesName = useGetSalesName(quote.sales_id ?? undefined, {
    enabled: quote.sales_id != null,
  });
  const [deleteQuote, { isPending }] = useDelete(
    "company_quotes",
    { id: quote.id, previousData: quote },
    {
      mutationMode: "pessimistic",
      onSuccess: () => {
        setConfirmOpen(false);
        notify("resources.companies.quotes.deleted");
        onDeleted();
      },
    },
  );
  const file = quote.attachments?.[0];
  if (!file) return null;

  return (
    <li className="flex items-center gap-3 py-2 text-sm">
      <FileText className="size-5 shrink-0 text-red-600" />
      <div className="flex-1 min-w-0">
        <a
          href={file.src}
          target="_blank"
          rel="noopener noreferrer"
          className="block font-medium truncate hover:underline"
        >
          {file.title}
        </a>
        <span className="text-muted-foreground">
          {formatLocalizedDate(quote.date, locale)}
          {salesName ? ` · ${salesName}` : ""}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={translate("resources.companies.quotes.delete", {
          name: file.title,
        })}
        onClick={() => setConfirmOpen(true)}
      >
        <Trash2 className="size-4" />
      </Button>
      <Confirm
        isOpen={isConfirmOpen}
        loading={isPending}
        title="resources.companies.quotes.delete_title"
        content="resources.companies.quotes.delete_content"
        translateOptions={{ name: file.title }}
        onConfirm={() => deleteQuote()}
        onClose={() => setConfirmOpen(false)}
      />
    </li>
  );
};
